import React, { useEffect, useState } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import UserCard from "../../components/userCard/userCard";
import PlayToFund from "../../components/playToFundCard/playToFund";
import CampaignDetails from "../../components/campaignDetails/campaignDetails";
import Loader from "../../components/common/loader/loader";
import useCampaignProfile from "./hooks/useCampaignProfile";
import BottomDrawer from "../../components/common/modal/BottomDrawer";
import UserProfile from "../../components/userProfile/userProfile";
import ShareComponent from "../../components/shareModal/ShareComponent";
import ColinkUsersCard from "../../components/colinkUsersCard/colinkUsersCard";
import useFollowEntity from "../../services/hooks/useFollowEntity";
import useFollowingEntity from "../../services/hooks/useFollowingEntity";
import useAuthStatus from "./hooks/useAuthStatus";
import useCampaignFunded from "./hooks/useCampaignFunded ";
import useCurrentUserStashLink from "./hooks/useCurrentUserStashLink";
import { getImpactLevel, getLocalStorage, getStyles, setLocalStorage } from "../../utils/common-function";
import { useHistory } from "react-router";
import { routes } from "../../routes/routes.constant";
import { defaultCoLinkUserLevel } from "../../utils/constants";
import useCheckStashLink from "./hooks/useCheckStashLink";
import useUser from "../../services/hooks/useUser";
import mixpanel from "../../services/mixpanelService";
import WaitlistModal from "../../components/waitlistModal";
import useEmojiBlast from "../../services/hooks/useEmojiBlast";
import { useMutation } from "@apollo/client";
import { VIEW_STASHLINK } from "../../queries";
import { ProtectedRoute } from "../../routes/helpers";
import { isNativeWebview } from "../../common/constants";

const CampaignProfile: React.FC = () => {
  const {
    isMatchSponsor,
    fetchBrands,
    stashlink,
    stashlinkLoading,
    campaignAttemptsLoading,
    isServerError,
    hasNoBrands,
    brandsChecked,
    setServerError
  } = useCampaignProfile();
  const user = stashlink?.user;
  const userId = getLocalStorage("userId");
  const { userData } = useUser(userId);
  const navigation = useHistory();
  const onboardingFeatureFlag = useFeatureIsOn("onboarding-flow");
  const campaign = stashlink?.campaign;
  const { isUserLoggedIn, currentUser } = useAuthStatus();
  const isCampaignFunded = useCampaignFunded(campaign);
  const isCurrentUserStashLink = useCurrentUserStashLink(currentUser?.user.id, user?.id);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);

  const [isWaitlistModalOpen, setWaitlistModalOpen] = useState<boolean>(false);
  const { isUserFollowing, refetch } = useFollowingEntity(currentUser?.user.id || "", user?.id || "");
  const { followCelebrity } = useFollowEntity(user?.id || "", refetch);
  const { startEmojiBlast } = useEmojiBlast(1500, 200);
  const isUserJoinedWaitlist = getLocalStorage("isUserJoinedWaitlist");
  const [viewStashlink] = useMutation(VIEW_STASHLINK);

  const isLoading = stashlinkLoading || campaignAttemptsLoading || !brandsChecked;

  setLocalStorage("stashlink", stashlink);
  useCheckStashLink(stashlinkLoading, stashlink);

  const currentUrl = window.location.href;
  const stashUser = {
    profilePhoto: user?.profilePhoto,
    fullName: user?.fullName,
    isPublicProfile: user?.isPublicProfile
  };

  let parentUser = null;
  if (stashlink?.parentStashlink) {
    parentUser = stashlink?.parentStashlink.user;
  }

  useEffect(() => {
    if (stashlink) {
      viewStashlink({
        variables: {
          stashlinkId: stashlink.id
        }
      });
    }
  }, []);

  const stashLinkUserImpactScoreLevel = parentUser ? defaultCoLinkUserLevel : user?.impactScore ? getImpactLevel(user?.impactScore) : "1";

  useEffect(() => {
    if (isCampaignFunded && !isLoading) {
      startEmojiBlast();
    }
  }, [isCampaignFunded, isLoading]);

  useEffect(() => {
    if (isServerError) {
      setWaitlistModalOpen(true);
      setServerError(false);
    }
  }, [isServerError]);

  if (isLoading || !stashlink) {
    return (
      <div className="h-[calc(100vh-80px)] w-screen flex justify-center items-center bg-soft-black">
        <Loader />
      </div>
    );
  }

  const closeWaitListModal = () => {
    setWaitlistModalOpen(false);
  };

  const closeUserProfileModal = () => {
    setIsUserProfileModalOpen(false);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  const handleFollowClick = async () => {
    if (!userData?.finishedOnBoarding) {
      navigation.push({ pathname: routes.callback, state: { from: location.pathname } });
    } else {
      await followCelebrity(true);
      // setChangeStatusFollowing(true);
    }
    mixpanel.track("follow-button-clicked");
  };

  const styles = getStyles(stashLinkUserImpactScoreLevel);

  return (
    <>
      {isNativeWebview() && (
        <PlayToFund
          campaign={campaign}
          userProfile={parentUser?.profilePhoto || user?.profilePhoto}
          celebrityName={parentUser?.fullName || user?.fullName}
          onToggle={fetchBrands}
          isUserLoggedIn={isUserLoggedIn}
          isCampaignFunded={isCampaignFunded}
          followCelebrity={handleFollowClick}
          isUserFollowing={isUserFollowing}
          isCurrentUserStashLink={isCurrentUserStashLink}
          setIsShareModalOpen={setIsShareModalOpen}
          setWaitlistModalOpen={setWaitlistModalOpen}
          onboardingFeatureFlag={onboardingFeatureFlag}
          isUserJoinedWaitlist={isUserJoinedWaitlist}
          hasNoBrands={hasNoBrands}
        />
      )}
      <div
        className="bg-black text-white min-h-screen flex flex-col items-center"
        style={
          isNativeWebview()
            ? {
              paddingBottom: 0
            }
            : {
              paddingBottom: 128
            }
        }>
        <WaitlistModal stashUser={parentUser || user} isOpen={isWaitlistModalOpen} onClose={closeWaitListModal} />
        {parentUser ? (
          <ColinkUsersCard
            parentUser={parentUser}
            colinkUser={user}
            isShareIconRequired={true}
            setIsShareModalOpen={setIsShareModalOpen}
            impactScoreLevel={stashLinkUserImpactScoreLevel}
          />
        ) : (
          <UserCard
            user={user}
            setIsUserProfileModalOpen={setIsUserProfileModalOpen}
            setIsShareModalOpen={setIsShareModalOpen}
            showIcons={true}
            impactScoreLevel={stashLinkUserImpactScoreLevel}
          />
        )}

        <div
          className={`w-full`}
          style={{
            background: `linear-gradient(to top, ${styles.levelColor} 30%, ${styles.levelColor} 85%, black)`
          }}>
          <div className={`bg-black/85 p-4 pt-0 ${isNativeWebview() ? 'pb-52' : "pb-8"}`}>
            <div className="h-[49px] w-full flex-col justify-center items-center gap-2 inline-flex z-10">
              <div className="self-stretch flex-col justify-start items-center gap-0 flex">
                <div className="text-neutral-50 text-base font-normal font-secondary leading-tight pb-1">is stashing for</div>
                <div className="self-stretch text-center text-neutral-50 text-22px font-bold font-primary leading-normal tracking-tight -mt-0.5">
                  {campaign?.charity?.name}
                </div>
              </div>
            </div>
            <div className="mt-5">
              <CampaignDetails campaign={campaign} impactScoreLevel={stashLinkUserImpactScoreLevel} isMatchSponsor={isMatchSponsor} />
            </div>
          </div>
        </div>
        {!isNativeWebview() && (
          <div className={"w-full bottom-0 sticky"}>
            <PlayToFund
              campaign={campaign}
              userProfile={parentUser?.profilePhoto || user?.profilePhoto}
              celebrityName={parentUser?.fullName || user?.fullName}
              onToggle={fetchBrands}
              isUserLoggedIn={isUserLoggedIn}
              isCampaignFunded={isCampaignFunded}
              followCelebrity={handleFollowClick}
              isUserFollowing={isUserFollowing}
              isCurrentUserStashLink={isCurrentUserStashLink}
              setIsShareModalOpen={setIsShareModalOpen}
              setWaitlistModalOpen={setWaitlistModalOpen}
              onboardingFeatureFlag={onboardingFeatureFlag}
              isUserJoinedWaitlist={isUserJoinedWaitlist}
              hasNoBrands={hasNoBrands}
            />
          </div>
        )}

        {isUserProfileModalOpen && (
          <BottomDrawer isOpen={isUserProfileModalOpen} onClose={closeUserProfileModal}>
            <UserProfile user={user} impactScoreLevel={stashLinkUserImpactScoreLevel} isOpen={isUserProfileModalOpen} />
          </BottomDrawer>
        )}
      </div>

      {isShareModalOpen && (
        <ShareComponent
          stashUser={parentUser || stashUser}
          charityName={campaign?.charity?.name}
          charityLogo={campaign?.charity?.imageUrl}
          currentAmount={campaign?.currentAmount}
          goalAmount={campaign?.goalAmount}
          shareUrl={currentUrl}
          coLinkUser={parentUser ? user : undefined}
          isOpen={isShareModalOpen}
          onClose={closeShareModal}
          impactScoreLevel={stashLinkUserImpactScoreLevel}
        />
      )}
    </>
  );
};

export default ProtectedRoute(CampaignProfile);
